import { createQuery } from "react-query-kit";
import { keepPreviousData, useQueries } from "@tanstack/react-query";
import { api } from "../../api";
import {
  GetCategoryAdsPreSearchQuery,
  SearchCategoryAdsRequest,
  GetCategoryAdsExternalResponse,
} from "../../contracts/category-ads";
import { LanguageCode } from "../../contracts/language-settings";
import { isCategoryAdOfferedTo } from "../../../utils/extraBeds";

export const fetchCategories = async (
  params: SearchCategoryAdsRequest & { siteId: string; filter: string },
) => {
  const { languageCode, arrivalDate, departureDate, guests, siteId, filter, code } =
    params;
  const body = {
    languageCode,
    arrivalDate,
    departureDate,
    guests: {
      adults: guests.adults,
      teenagers: guests.teenagers,
      children: guests.children?.map((child) => ({
        age: child.age,
        ageType: child.ageType,
        bedCapacityType: child.bedCapacityType,
      })),
      infants: guests.infants?.map((infant) => ({
        age: infant.age,
        ageType: infant.ageType,
        bedCapacityType: infant.bedCapacityType,
      })),
    },
    // WBE booking code re-prices the catalog when present (CXA-385).
    ...(code ? { code } : {}),
  };

  const query = new URLSearchParams({
    ...(languageCode ? { languageCode } : {}),
    filter: filter ?? "none",
  });

  return api.post<GetCategoryAdsExternalResponse>(
    `/api/public/category-ads/${siteId}?${query.toString()}`,
    body,
  );
};

// Bump when the CategoryAdDetailed shape changes in a way that
// old cached responses can't satisfy — old client builds cache the
// previous shape under the previous key and would serve stale data
// indefinitely otherwise. Bump revs:
//   v2 → added `categoryId` (link to operational `categories.id`)
//   v3 → added `extraBeds`, `cot` and `capacity.extraBeds`
const CATEGORY_ADS_SCHEMA_REV = "v3";

// POST /api/wbe-external/sites/{siteId}/category-ads (Get categories)
export const useGetCategories = createQuery<
  GetCategoryAdsExternalResponse,
  SearchCategoryAdsRequest & { siteId: string; filter: string }
>({
  queryKey: ["useGetCategories", CATEGORY_ADS_SCHEMA_REV],
  fetcher: fetchCategories,
  enabled: false,
});

// GET /api/public/category-ads/{siteId}/pre-search (Get categories before search)
export const useGetCategoriesPreSearch = createQuery<
  GetCategoryAdsExternalResponse,
  GetCategoryAdsPreSearchQuery & { siteId: string; filter?: string }
>({
  queryKey: ["useGetCategoriesPreSearch", CATEGORY_ADS_SCHEMA_REV],
  fetcher: async ({ siteId, languageCode, filter, categoryAdId }) => {
    return api.get<GetCategoryAdsExternalResponse>(
      `/api/public/category-ads/${siteId}/pre-search`,
      {
        languageCode,
        filter,
        categoryAdId,
      },
    );
  },
});

interface UnitCategoriesQueryInput {
  unitId: string;
  variables: SearchCategoryAdsRequest & { languageCode: LanguageCode };
}

interface UseGetCategoriesByUnitsParams {
  queryInputs: UnitCategoriesQueryInput[];
  siteId: string;
  filter: string;
  searchToken: number;
  enabled: boolean;
  refetchIntervalMs?: number;
  /**
   * Keep the previous key's data visible while a bumped `searchToken` makes
   * a new query key, instead of that new key's fresh pending/`undefined` —
   * a caller that forces a refetch on an unchanged search (v2's Search
   * button) needs this so it isn't skeletoning cards already on screen.
   * Opt-in: existing callers keep the plain new-key-means-fresh-pending
   * behavior by leaving this unset.
   */
  keepPreviousDataOnKeyChange?: boolean;
  /** Extra beds the cart's rooms hold per category for the searched stay. */
  heldExtraBeds?: ReadonlyMap<string, number>;
}

const NO_HELD_CATEGORIES: ReadonlyMap<string, number> = new Map();

export const useGetCategoriesByUnits = ({
  queryInputs,
  siteId,
  filter,
  searchToken,
  enabled,
  refetchIntervalMs,
  keepPreviousDataOnKeyChange,
  heldExtraBeds = NO_HELD_CATEGORIES,
}: UseGetCategoriesByUnitsParams) => {
  return useQueries({
    queries: queryInputs.map(({ unitId, variables }) => {
      const queryVariables = {
        ...variables,
        siteId,
        filter,
      };
      const options = useGetCategories.getOptions(queryVariables);
      return {
        ...options,
        queryKey: [...useGetCategories.getKey(queryVariables), unitId, searchToken],
        enabled,
        select: (ads: GetCategoryAdsExternalResponse) =>
          ads.filter((ad) => isCategoryAdOfferedTo(ad, heldExtraBeds)),
        staleTime: 10_000,
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
        refetchInterval: refetchIntervalMs,
        placeholderData: keepPreviousDataOnKeyChange ? keepPreviousData : undefined,
      };
    }),
  });
};
